import axios, { AxiosError } from 'axios';
import { useAuthStore } from '@/store/auth';
import toast from 'react-hot-toast';

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api',
  withCredentials: true,
  timeout: 20_000,
});

api.interceptors.request.use(async (config) => {
  let token = useAuthStore.getState().accessToken;
  // After a page reload the access token (held only in memory) is gone, but the
  // persisted `user` tells us we are still signed in. Fetch a fresh token BEFORE
  // the request leaves, so protected calls do not each 401 and bounce through a
  // refresh + retry. One refresh is shared across every call a page fires.
  if (!token && useAuthStore.getState().user) {
    token = await refreshOnce();
  }
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

/**
 * Work out WHY a request got no response.
 *
 * Axios reports "network error" identically whether the API is down, the
 * browser blocked it on CORS, or the request timed out - so we probe the
 * health endpoint (which needs no auth) and infer from what happens.
 *
 *   • health responds        → the API is up; the original call was blocked
 *                              by CORS or died mid-flight.
 *   • fetch throws           → nothing is listening; the API really is down.
 */
export async function diagnoseNoResponse(): Promise<string> {
  const base = API_BASE.replace(/\/$/, '');
  const url = `${base}/health`;

  // Attempt 1 - a normal CORS request. If this succeeds the server is up AND
  // is willing to talk to this origin, so the fault is elsewhere.
  try {
    const res = await fetch(url, { method: 'GET', mode: 'cors', cache: 'no-store' });
    if (res.ok) {
      return `The server is up and reachable, but the login request itself failed. Check the backend terminal - the error will be printed there.`;
    }
    return `The server answered with ${res.status} on ${url}. Check the backend terminal for the error.`;
  } catch {
    /* fall through - a CORS rejection throws exactly like a dead server */
  }

  // Attempt 2 - no-cors. The browser refuses to show us the body, but it will
  // still make the connection, so this succeeds whenever something is actually
  // listening. That is what separates "blocked" from "not running" - without
  // it, a CORS rejection is indistinguishable from a dead port.
  try {
    await fetch(url, { method: 'GET', mode: 'no-cors', cache: 'no-store' });
    return `The server IS running, but it rejected this page's origin (${window.location.origin}). Set CORS_ORIGIN=${window.location.origin} in backend/.env, then restart the backend.`;
  } catch {
    /* nothing listening at all */
  }

  const port = (() => { try { return new URL(base, window.location.origin).port || '4000'; } catch { return '4000'; } })();
  return `Nothing is listening on ${url}. Start the backend: cd backend && npm run dev - it should print "Souqli API listening on :${port}". If it exits instead, the error it prints is the real problem.`;
}

let refreshing: Promise<string | null> | null = null;

/** Single-flight refresh: concurrent callers share one /auth/refresh call. */
function refreshOnce(): Promise<string | null> {
  refreshing ??= refreshAccessToken().finally(() => { refreshing = null; });
  return refreshing;
}

let announcedLockout = false;

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as any;
    const code = (error.response?.data as any)?.code;

    // Account went from active to suspended/blocked mid-session - every
    // subsequent authenticated call now 403s server-side, however fresh the
    // access token looks. Kick the user out everywhere, once, rather than
    // toasting the same message on every in-flight request.
    if (code === 'ACCOUNT_SUSPENDED' || code === 'ACCOUNT_BLOCKED') {
      useAuthStore.getState().clear();
      if (!announcedLockout) {
        announcedLockout = true;
        toast.error(
          code === 'ACCOUNT_BLOCKED' ? 'This account has been blocked.' : 'This account has been suspended.',
          { duration: 9000 },
        );
        if (window.location.pathname !== '/login') window.location.assign('/login');
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !original?._retry) {
      original._retry = true;
      const next = await refreshOnce();
      if (next) {
        original.headers.Authorization = `Bearer ${next}`;
        return api.request(original);
      }
      useAuthStore.getState().clear();
    }
    return Promise.reject(error);
  },
);

async function refreshAccessToken(): Promise<string | null> {
  try {
    const { data } = await axios.post(
      `${import.meta.env.VITE_API_URL ?? '/api'}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    useAuthStore.getState().setAccessToken(data.accessToken);
    return data.accessToken as string;
  } catch {
    return null;
  }
}
